import { View, Text, styled, ScrollView, useTheme } from 'tamagui'
import { Calendar, X, Users, MapPin } from '@tamagui/lucide-icons'
import { useState } from 'react'
import { useAppStore } from '@/store'
import { scheduledEvents, users } from '@/mocks'
import { Avatar } from '../../common'

const Overlay = styled(View, {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: '$overlay',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 3000,
})

const Panel = styled(View, {
  width: 480,
  maxHeight: 560,
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusXl',
  borderWidth: 1,
  borderColor: '$borderDefault',
  flexDirection: 'column',
  overflow: 'hidden',
})

const Header = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingHorizontal: 20,
  paddingVertical: 16,
  borderBottomWidth: 1,
  borderBottomColor: '$borderDefault',
})

const HeaderLeft = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 10,
})

const Title = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 16,
  color: '$surfaceForeground',
})

const CloseButton = styled(View, {
  width: 28,
  height: 28,
  borderRadius: '$borderRadiusMd',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 150ms ease' },
})

const EventCard = styled(View, {
  backgroundColor: '$sidebar',
  borderRadius: '$borderRadiusLg',
  borderWidth: 1,
  borderColor: '$borderDefault',
  padding: 16,
  gap: 10,
})

const EventTitle = styled(Text, {
  fontFamily: '$fontHeading',
  fontWeight: '600',
  fontSize: 15,
  color: '$surfaceForeground',
})

const EventDescription = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  color: '$sidebarMuted',
  lineHeight: 18,
})

const MetaRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 6,
})

const MetaText = styled(Text, {
  fontFamily: '$fontMono',
  fontSize: 12,
  color: '$color8',
})

const RsvpRow = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
})

const AvatarStack = styled(View, {
  flexDirection: 'row',
})

const EmptyState = styled(View, {
  padding: 40,
  alignItems: 'center',
  gap: 12,
})

const EmptyText = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 14,
  color: '$sidebarMuted',
  textAlign: 'center',
})

const InterestedButton = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  gap: 4,
  height: 28,
  paddingHorizontal: 10,
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  cursor: 'pointer',
  variants: {
    active: {
      true: {
        borderColor: '$accent',
        backgroundColor: 'rgba(62, 207, 207, 0.1)',
      },
      false: {
        borderColor: '$borderDefault',
        backgroundColor: 'transparent',
        hoverStyle: { backgroundColor: '$sidebarHover' },
      },
    },
  } as const,
})

function formatEventDate(timestamp: number): string {
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

function formatEventTime(timestamp: number): string {
  const date = new Date(timestamp)
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function ScheduledEventsPanel() {
  const theme = useTheme()
  const { activeServerId, setScheduledEventsOpen } = useAppStore()
  const [interestedEvents, setInterestedEvents] = useState<Set<string>>(new Set())

  const toggleInterested = (eventId: string) => {
    setInterestedEvents(prev => {
      const next = new Set(prev)
      if (next.has(eventId)) {
        next.delete(eventId)
      } else {
        next.add(eventId)
      }
      return next
    })
  }

  const events = activeServerId ? scheduledEvents[activeServerId] || [] : []
  const sortedEvents = [...events].sort((a, b) => a.startAt - b.startAt)

  return (
    <Overlay onPress={() => setScheduledEventsOpen(false)}>
      <Panel
        onPress={(e: { stopPropagation: () => void }) => e.stopPropagation()}
        // @ts-expect-error -- web box-shadow
        style={{ boxShadow: theme.shadowModal?.val || 'none' }}
      >
        <Header>
          <HeaderLeft>
            <Calendar size={18} color="$accent" />
            <Title>Scheduled Events</Title>
          </HeaderLeft>
          <CloseButton onPress={() => setScheduledEventsOpen(false)}>
            <X size={16} color="$sidebarMuted" />
          </CloseButton>
        </Header>

        <ScrollView flex={1} padding={16}>
          <View gap={12}>
            {sortedEvents.length === 0 ? (
              <EmptyState>
                <Calendar size={32} color="$sidebarMuted" />
                <EmptyText>No upcoming events</EmptyText>
              </EmptyState>
            ) : (
              sortedEvents.map((event) => {
                const rsvpUsers = event.rsvpUserIds
                  .map((id) => users[id])
                  .filter(Boolean)

                return (
                  <EventCard key={event.id}>
                    <MetaRow>
                      <Calendar size={12} color="$accent" />
                      <MetaText>
                        {formatEventDate(event.startAt)} at {formatEventTime(event.startAt)}
                        {event.endAt && ` - ${formatEventTime(event.endAt)}`}
                      </MetaText>
                    </MetaRow>

                    <EventTitle>{event.title}</EventTitle>

                    {event.description && (
                      <EventDescription>{event.description}</EventDescription>
                    )}

                    {event.channelId && (
                      <MetaRow>
                        <MapPin size={12} color="$sidebarMuted" />
                        <MetaText>Voice channel</MetaText>
                      </MetaRow>
                    )}

                    <RsvpRow>
                      <InterestedButton
                        active={interestedEvents.has(event.id)}
                        onPress={() => toggleInterested(event.id)}
                      >
                        <Text fontFamily="$fontBody" fontSize={12} color={interestedEvents.has(event.id) ? '$accent' : '$sidebarMuted'}>
                          {interestedEvents.has(event.id) ? 'Interested' : 'Interested?'}
                        </Text>
                      </InterestedButton>
                      <AvatarStack>
                        {rsvpUsers.slice(0, 5).map((user, i) => (
                          <View
                            key={user.id}
                            marginLeft={i > 0 ? -8 : 0}
                            borderWidth={2}
                            borderColor="$sidebar"
                            borderRadius="$borderRadiusFull"
                            zIndex={5 - i}
                          >
                            <Avatar
                              name={user.displayName}
                              color={user.roleColor || '#2A3142'}
                              size="xs"
                            />
                          </View>
                        ))}
                      </AvatarStack>
                      <MetaRow>
                        <Users size={12} color="$sidebarMuted" />
                        <MetaText>
                          {event.rsvpUserIds.length + (interestedEvents.has(event.id) ? 1 : 0)} interested
                        </MetaText>
                      </MetaRow>
                    </RsvpRow>
                  </EventCard>
                )
              })
            )}
          </View>
        </ScrollView>
      </Panel>
    </Overlay>
  )
}
